import React from 'react'
import {
  Box,
  Button,
  Fade,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Paper,
  Popper,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { Link, useLocation } from 'react-router-dom'
import MenuIcon from '@mui/icons-material/Menu'
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined'
import StyleOutlinedIcon from '@mui/icons-material/StyleOutlined'
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined'
import OutboundOutlinedIcon from '@mui/icons-material/OutboundOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import AssignmentReturnOutlinedIcon from '@mui/icons-material/AssignmentReturnOutlined'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined'
import StraightenOutlinedIcon from '@mui/icons-material/StraightenOutlined'
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined'
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline'
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined'
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined'
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined'
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined'
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined'
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined'
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined'
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined'
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined'
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined'
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined'
import BookmarksOutlinedIcon from '@mui/icons-material/BookmarksOutlined'
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined'
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import PlaylistAddCheckOutlinedIcon from '@mui/icons-material/PlaylistAddCheckOutlined'
import AllInboxOutlinedIcon from '@mui/icons-material/AllInboxOutlined'
import AltRouteOutlinedIcon from '@mui/icons-material/AltRouteOutlined'
import MapOutlinedIcon from '@mui/icons-material/MapOutlined'
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined'
import { isSuperAdmin } from '@/utils/authUtils'
import DomainOutlinedIcon from '@mui/icons-material/DomainOutlined'
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined'
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import routerConstants from '@/constants/routerConstants'
import { resolveBanOnlineLanding } from '@/components/admin/banOnline/partials/BanOnlineModuleNav'
import { uiTestSystemMenuItem } from '@/modules/ui-test'
import { testModuleChiTietMenuItem } from '@/modules/test-module-chitiet'
import { ALL_CHUCNANG_PERMISSION_CODES } from '@/constants/permissionConstants'
import { useI18n } from '@/contexts/I18nContext'
import { useMenuChucNang } from '@/hooks/useMenuChucNang'
import { useCurrentChiNhanhId } from '@/hooks/useCurrentChiNhanhId'
import { withCurrentChiNhanhQuery } from '@/utils/vanHanhMenuPathUtils'
import { banhangKiotLike, buildKiotHeaderNavButtonSx } from '@/components/layout/banhangKiotTokens'
import { enableVanHanh } from '@/config'

type AppPermission = (typeof ALL_CHUCNANG_PERMISSION_CODES)[keyof typeof ALL_CHUCNANG_PERMISSION_CODES]

const topNavIconSx = { fontSize: banhangKiotLike.kiotNavIconSizePx } as const

export type BanhangNavDropdownItem = {
  id: string
  labelKey: string
  to: string
  /** Icon trái (menu con kiểu Kiot) */
  icon?: React.ReactNode
  /** Có ít nhất 1 trong các quyền này (OR). */
  permissions?: AppPermission[]
  /** Phải có đủ tất cả quyền này (AND) — vd: báo cáo LN cần thêm xem giá vốn. */
  requireAllPermissions?: AppPermission[]
  /** Chỉ hiện với role `super_admin` (màn DEV). */
  superAdminOnly?: boolean
  /** Chỉ hiện khi bật module Vận hành (`REACT_APP_ENABLE_VANHANH`). */
  vanHanhOnly?: boolean
  /** Nhóm con trong dropdown (hiển thị subheader) */
  groupKey?: string
}

export type BanhangTopMenuEntry =
  | {
      id: string
      type: 'link'
      labelKey: string
      to: string
      icon: React.ReactNode
      permissions?: AppPermission[]
    }
  | {
      id: string
      type: 'dropdown'
      labelKey: string
      icon: React.ReactNode
      items: BanhangNavDropdownItem[]
    }

export const banhangTopMenu: BanhangTopMenuEntry[] = [
  {
    id: 'dashboard',
    type: 'link',
    labelKey: 'components.menu.dashboard',
    to: routerConstants.dashboard,
    icon: <DashboardOutlinedIcon sx={topNavIconSx} />,
    permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_DASHBOARD_LIST],
  },
  {
    id: 'salesMenu',
    type: 'dropdown',
    labelKey: 'components.menu.salesSection',
    icon: <ReceiptLongOutlinedIcon sx={topNavIconSx} />,
    items: [
      {
        id: 'datHang',
        to: routerConstants.datHang.list,
        labelKey: 'components.menu.datHangList',
        icon: <ReceiptLongOutlinedIcon sx={{ fontSize: 18 }} />,
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_DAT_HANG_LIST],
      },
      {
        id: 'hoaDon',
        to: routerConstants.hoaDon.list,
        labelKey: 'components.menu.hoaDonList',
        icon: <ReceiptLongOutlinedIcon sx={{ fontSize: 18 }} />,
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_HOA_DON_LIST],
      },
      {
        id: 'maVanDon',
        to: routerConstants.maVanDon.list,
        labelKey: 'components.menu.maVanDonList',
        icon: <LocalShippingOutlinedIcon sx={{ fontSize: 18 }} />,
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_MA_VAN_DON_LIST],
      },
      {
        id: 'traHang',
        to: routerConstants.traHang.list,
        labelKey: 'components.menu.traHangList',
        icon: <AssignmentReturnOutlinedIcon sx={{ fontSize: 18 }} />,
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_TRA_HANG_LIST],
      },
      {
        id: 'yeuCauSuaChua',
        to: routerConstants.yeuCauSuaChua.list,
        labelKey: 'components.menu.yeuCauSuaChuaList',
        icon: <BuildOutlinedIcon sx={{ fontSize: 18 }} />,
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_YEU_CAU_SUA_CHUA_LIST],
      },
      {
        id: 'danhMucThuKhac',
        to: routerConstants.danhMucThuKhac.list,
        labelKey: 'components.menu.danhMucThuKhacList',
        icon: <PaidOutlinedIcon sx={{ fontSize: 18 }} />,
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_DANH_MUC_THU_KHAC_LIST],
      },
      {
        id: 'yeuCauXuatHoaDon',
        to: routerConstants.yeuCauXuatHoaDon.list,
        labelKey: 'components.menu.yeuCauXuatHoaDonList',
        icon: <FactCheckOutlinedIcon sx={{ fontSize: 18 }} />,
        // Any permission: NVBH (WEB_BH_YCX_LIST) hoặc KT (WEB_BH_YCX_LIST_KT) đều xem được
        // trang tổng hợp. Filter theo trạng thái ở sidebar.
        permissions: [
          ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_YCX_LIST,
          ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_YCX_LIST_KT,
        ],
      },
      {
        id: 'xuatHuy',
        to: routerConstants.xuatHuy.list,
        labelKey: 'components.menu.xuatHuyList',
        icon: <DeleteSweepOutlinedIcon sx={{ fontSize: 18 }} />,
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_PHIEU_XUAT_KHO_LIST],
      },
      {
        id: 'xuatTraNcc',
        to: routerConstants.xuatTraNcc.list,
        labelKey: 'components.menu.xuatTraNccList',
        icon: <HandshakeOutlinedIcon sx={{ fontSize: 18 }} />,
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_PHIEU_XUAT_KHO_LIST],
      },
    ],
  },
  {
    id: 'productsMenu',
    type: 'dropdown',
    labelKey: 'components.menu.productsSection',
    icon: <InventoryOutlinedIcon sx={topNavIconSx} />,
    items: [
      {
        id: 'nhomHang',
        to: `${routerConstants.nhomHang.list}?kieu=cay`,
        labelKey: 'components.menu.nhomHangList',
        icon: <CategoryOutlinedIcon sx={{ fontSize: 18 }} />,
        groupKey: 'components.menu.productMenuCatalog',
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_NHOM_HANG_LIST],
      },
      {
        id: 'products',
        to: routerConstants.product.list,
        labelKey: 'components.menu.products',
        icon: <InventoryOutlinedIcon sx={{ fontSize: 18 }} />,
        groupKey: 'components.menu.productMenuCatalog',
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_PRODUCT_LIST],
      },
      {
        id: 'phanLoaiSanPham',
        to: routerConstants.phanLoaiSanPham.list,
        labelKey: 'components.menu.phanLoaiSanPhamList',
        icon: <LabelOutlinedIcon sx={{ fontSize: 18 }} />,
        groupKey: 'components.menu.productMenuCatalog',
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_PHAN_LOAI_SAN_PHAM_LIST],
      },
      {
        id: 'thongTinVatHangHoa',
        to: routerConstants.thongTinVatHangHoa.list,
        labelKey: 'components.menu.thongTinVatHangHoaList',
        icon: <ReceiptLongOutlinedIcon sx={{ fontSize: 18 }} />,
        groupKey: 'components.menu.productMenuCatalog',
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_THONG_TIN_VAT_HANG_HOA_LIST],
      },
      {
        id: 'viTri',
        to: routerConstants.viTri.list,
        labelKey: 'components.menu.viTriList',
        icon: <PlaceOutlinedIcon sx={{ fontSize: 18 }} />,
        groupKey: 'components.menu.productMenuSetup',
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_VI_TRI_LIST],
      },
      {
        id: 'donViTinh',
        to: routerConstants.donViTinh.list,
        labelKey: 'components.menu.donViTinhList',
        icon: <StraightenOutlinedIcon sx={{ fontSize: 18 }} />,
        groupKey: 'components.menu.productMenuSetup',
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_DON_VI_TINH_LIST],
      },
      {
        id: 'thuongHieu',
        to: routerConstants.thuongHieu.list,
        labelKey: 'components.menu.thuongHieuList',
        icon: <LocalOfferOutlinedIcon sx={{ fontSize: 18 }} />,
        groupKey: 'components.menu.productMenuSetup',
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_THUONG_HIEU_LIST],
      },
      {
        id: 'thuocTinh',
        to: routerConstants.thuocTinh.list,
        labelKey: 'components.menu.thuocTinhList',
        icon: <StyleOutlinedIcon sx={{ fontSize: 18 }} />,
        groupKey: 'components.menu.productMenuSetup',
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_THUOC_TINH_LIST],
      },
      {
        id: 'bangGia',
        to: routerConstants.bangGia.list,
        labelKey: 'components.menu.bangGiaList',
        icon: <AttachMoneyOutlinedIcon sx={{ fontSize: 18 }} />,
        groupKey: 'components.menu.productMenuSetup',
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_BANG_GIA_LIST],
      },
    ],
  },
  {
    id: 'partnersMenu',
    type: 'dropdown',
    labelKey: 'components.menu.partnersSection',
    icon: <HandshakeOutlinedIcon sx={topNavIconSx} />,
    items: [
      {
        id: 'nhomKh',
        to: routerConstants.nhomKhachHang.list,
        labelKey: 'components.menu.nhomKhachHangList',
        icon: <GroupsOutlinedIcon sx={{ fontSize: 18 }} />,
        groupKey: 'components.menu.customersSection',
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_NHOM_KHACH_HANG_LIST],
      },
      {
        id: 'khachHang',
        to: routerConstants.khachHang.list,
        labelKey: 'components.menu.khachHangList',
        icon: <PeopleOutlineIcon sx={{ fontSize: 18 }} />,
        groupKey: 'components.menu.customersSection',
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_KHACH_HANG_LIST],
      },
      {
        id: 'diaChiNhanHang',
        to: routerConstants.diaChiNhanHang.all,
        labelKey: 'components.menu.diaChiNhanHangList',
        icon: <PlaceOutlinedIcon sx={{ fontSize: 18 }} />,
        groupKey: 'components.menu.customersSection',
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_DIA_CHI_NHAN_HANG_LIST],
      },
      {
        id: 'thongTinXuatHoaDon',
        to: routerConstants.thongTinXuatHoaDon.all,
        labelKey: 'components.menu.thongTinXuatHoaDonList',
        icon: <ReceiptLongOutlinedIcon sx={{ fontSize: 18 }} />,
        groupKey: 'components.menu.customersSection',
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_THONG_TIN_XUAT_HOA_DON_LIST],
      },
      {
        id: 'ncc',
        to: routerConstants.nhaCungCap.list,
        labelKey: 'components.menu.nhaCungCapList',
        icon: <DomainOutlinedIcon sx={{ fontSize: 18 }} />,
        groupKey: 'components.menu.suppliersSection',
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_NHA_CUNG_CAP_LIST],
      },
    ],
  },
  {
    id: 'tonKhoMenu',
    type: 'dropdown',
    labelKey: 'components.menu.tonKho',
    icon: <WarehouseOutlinedIcon sx={topNavIconSx} />,
    items: [
      {
        id: 'phieuNhanHang',
        to: routerConstants.phieuNhanHang.list,
        labelKey: 'components.menu.phieuNhanHangList',
        icon: <InboxOutlinedIcon sx={{ fontSize: 18 }} />,
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_PHIEU_NHAN_HANG_LIST],
      },
      {
        id: 'phieuNhapKho',
        to: routerConstants.phieuNhapKho.list,
        labelKey: 'components.menu.phieuNhapKhoList',
        icon: <InventoryOutlinedIcon sx={{ fontSize: 18 }} />,
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_PHIEU_NHAP_KHO_LIST],
      },
      {
        id: 'phieuXuatKho',
        to: routerConstants.phieuXuatKho.list,
        labelKey: 'components.menu.phieuXuatKhoList',
        icon: <OutboundOutlinedIcon sx={{ fontSize: 18 }} />,
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_PHIEU_XUAT_KHO_LIST],
      },
      {
        id: 'chuyenHang',
        to: routerConstants.chuyenHang.list,
        labelKey: 'components.menu.chuyenHangList',
        icon: <SwapHorizOutlinedIcon sx={{ fontSize: 18 }} />,
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_CHUYEN_HANG_LIST],
      },
      {
        id: 'kiemKe',
        to: routerConstants.kiemKe.list,
        labelKey: 'components.menu.kiemKeList',
        icon: <FactCheckOutlinedIcon sx={{ fontSize: 18 }} />,
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_KIEM_KE_LIST],
      },
    ],
  },
  {
    id: 'congQuyMenu',
    type: 'dropdown',
    labelKey: 'components.menu.congQuyGroup',
    icon: <AccountBalanceOutlinedIcon sx={topNavIconSx} />,
    items: [
      {
        id: 'congNo',
        to: routerConstants.congNo.khachHang,
        labelKey: 'components.menu.congNo',
        icon: <PaidOutlinedIcon sx={{ fontSize: 18 }} />,
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_CONG_NO_KHACH_HANG_LIST],
      },
      {
        id: 'soQuyPhieuThu',
        to: routerConstants.soQuy.phieuThu,
        labelKey: 'components.menu.soQuyPhieuThu',
        icon: <ArrowUpwardOutlinedIcon sx={{ fontSize: 18 }} />,
        groupKey: 'components.menu.soQuyGroup',
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_SO_QUY_THU_VIEW],
      },
      {
        id: 'soQuyPhieuChi',
        to: routerConstants.soQuy.phieuChi,
        labelKey: 'components.menu.soQuyPhieuChi',
        icon: <ArrowDownwardOutlinedIcon sx={{ fontSize: 18 }} />,
        groupKey: 'components.menu.soQuyGroup',
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_SO_QUY_CHI_VIEW],
      },
      {
        id: 'soQuyBaoCao',
        to: routerConstants.soQuy.baoCao,
        labelKey: 'components.menu.soQuyBaoCao',
        icon: <AssessmentOutlinedIcon sx={{ fontSize: 18 }} />,
        groupKey: 'components.menu.soQuyGroup',
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_SO_QUY_BAO_CAO_VIEW],
      },
      {
        id: 'soQuyTaiKhoanQuy',
        to: routerConstants.soQuy.taiKhoanQuy,
        labelKey: 'components.menu.soQuyTaiKhoanQuy',
        icon: <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 18 }} />,
        groupKey: 'components.menu.soQuyGroup',
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_TAI_KHOAN_QUY_MANAGE],
      },
      {
        id: 'soQuyLoaiThuChi',
        to: routerConstants.soQuy.loaiThuChi,
        labelKey: 'components.menu.soQuyLoaiThuChi',
        icon: <CategoryOutlinedIcon sx={{ fontSize: 18 }} />,
        groupKey: 'components.menu.soQuyGroup',
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_LOAI_THU_CHI_MANAGE],
      },
    ],
  },
  {
    id: 'baoCaoMenu',
    type: 'dropdown',
    labelKey: 'components.menu.baoCaoSection',
    icon: <AssessmentOutlinedIcon sx={topNavIconSx} />,
    items: [
      {
        id: 'baoCaoDoanhThuLn',
        to: routerConstants.baoCao.doanhThuLn,
        labelKey: 'components.menu.baoCaoDoanhThuLn',
        icon: <TrendingUpOutlinedIcon sx={{ fontSize: 18 }} />,
        groupKey: 'components.menu.baoCaoBanHangGroup',
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_BAO_CAO_DOANH_THU_LN],
      },
      {
        id: 'baoCaoTonKho',
        to: routerConstants.baoCao.tonKho,
        labelKey: 'components.menu.baoCaoTonKho',
        icon: <WarehouseOutlinedIcon sx={{ fontSize: 18 }} />,
        groupKey: 'components.menu.baoCaoBanHangGroup',
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_BAO_CAO_TON_KHO],
      },
      {
        id: 'baoCaoTopSale',
        to: routerConstants.baoCao.topSale,
        labelKey: 'components.menu.baoCaoTopSale',
        icon: <EmojiEventsOutlinedIcon sx={{ fontSize: 18 }} />,
        groupKey: 'components.menu.baoCaoBanHangGroup',
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_BAO_CAO_TOP_SALE],
      },
      {
        id: 'baoCaoTopMua',
        to: routerConstants.baoCao.topMua,
        labelKey: 'components.menu.baoCaoTopMua',
        icon: <EmojiEventsOutlinedIcon sx={{ fontSize: 18 }} />,
        groupKey: 'components.menu.baoCaoBanHangGroup',
        permissions: [
          ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_BAO_CAO_TOP_MUA,
          ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_BAO_CAO_TOP_SALE,
        ],
      },
      {
        id: 'baoCaoDaLuu',
        to: routerConstants.baoCao.daLuu,
        labelKey: 'components.menu.baoCaoDaLuu',
        icon: <BookmarksOutlinedIcon sx={{ fontSize: 18 }} />,
        groupKey: 'components.menu.baoCaoBanHangGroup',
        permissions: [
          ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_BAO_CAO_DA_LUU,
          ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_BAO_CAO_DOANH_THU_LN,
          ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_BAO_CAO_TON_KHO,
          ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_BAO_CAO_TOP_SALE,
          ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_BAO_CAO_TOP_MUA,
        ],
      },
      {
        id: 'baoCaoChiTiet',
        to: routerConstants.baoCaoKho.list,
        labelKey: 'components.menu.baoCaoChiTietDonHang',
        icon: <TableChartOutlinedIcon sx={{ fontSize: 18 }} />,
        groupKey: 'components.menu.baoCaoKhoGroup',
        vanHanhOnly: true,
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_KHO_BAO_CAO],
      },
      {
        id: 'baoCaoDonVanHanh',
        to: routerConstants.baoCaoKho.orderList,
        labelKey: 'components.menu.baoCaoDonVanHanh',
        icon: <AssessmentOutlinedIcon sx={{ fontSize: 18 }} />,
        groupKey: 'components.menu.baoCaoKhoGroup',
        vanHanhOnly: true,
        permissions: [
          ALL_CHUCNANG_PERMISSION_CODES.WEB_KHO_BAO_CAO_DON,
          ALL_CHUCNANG_PERMISSION_CODES.WEB_KHO_BAO_CAO,
        ],
      },
    ],
  },
  {
    id: 'banOnlineMenu',
    type: 'link',
    labelKey: 'components.menu.banOnline',
    to: routerConstants.donHangOnline.list,
    icon: <StorefrontOutlinedIcon sx={topNavIconSx} />,
    // Mục con chuyển sang sidebar trong module; TopNav chỉ còn 1 nút.
    permissions: [
      ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_DON_HANG_ONLINE_LIST,
      ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_HANG_HOA_LIEN_KET_LIST,
      ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_DOI_SOAT_ONLINE_LIST,
      ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_GIAN_HANG_ONLINE_LIST,
    ],
  },
  {
    id: 'vanHanhMenu',
    type: 'dropdown',
    labelKey: 'components.menu.vanHanhSection',
    icon: <LocalShippingOutlinedIcon sx={topNavIconSx} />,
    items: [
      {
        id: 'vanHanhDonHang',
        to: routerConstants.vanHanh.list,
        labelKey: 'components.menu.vanHanhDonHang',
        icon: <Inventory2OutlinedIcon sx={{ fontSize: 18 }} />,
        groupKey: 'components.menu.vanHanhSection',
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_KHO_DON_TAT_CA],
      },
      {
        id: 'vanHanhXepNhat',
        to: routerConstants.vanHanh.xepNhat,
        labelKey: 'components.menu.vanHanhXepNhat',
        icon: <PlaylistAddCheckOutlinedIcon sx={{ fontSize: 18 }} />,
        groupKey: 'components.menu.vanHanhSection',
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_KHO_NHAT_XEM],
      },
      {
        id: 'vanHanhKiemTra',
        to: routerConstants.vanHanh.kiemTra,
        labelKey: 'components.menu.vanHanhKiemTra',
        icon: <FactCheckOutlinedIcon sx={{ fontSize: 18 }} />,
        groupKey: 'components.menu.vanHanhSection',
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_KHO_KIEM_XEM],
      },
      {
        id: 'vanHanhDongGoi',
        to: routerConstants.vanHanh.dongGoi,
        labelKey: 'components.menu.vanHanhDongGoi',
        icon: <AllInboxOutlinedIcon sx={{ fontSize: 18 }} />,
        groupKey: 'components.menu.vanHanhSection',
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_KHO_DONG_XEM],
      },
      {
        id: 'vanHanhDieuPhoi',
        to: routerConstants.vanHanh.dieuPhoi,
        labelKey: 'components.menu.vanHanhDieuPhoi',
        icon: <AltRouteOutlinedIcon sx={{ fontSize: 18 }} />,
        groupKey: 'components.menu.vanHanhSection',
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_KHO_DIEU_PHOI_XEM],
      },
      {
        id: 'banDoGiaoHang',
        to: routerConstants.vanHanh.banDoGiaoHang,
        labelKey: 'components.menu.banDoGiaoHang',
        icon: <MapOutlinedIcon sx={{ fontSize: 18 }} />,
        groupKey: 'components.menu.vanHanhSection',
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_KHO_DIEU_PHOI_XEM],
      },
      {
        id: 'vanHanhGiaoHang',
        to: routerConstants.vanHanh.giaoHang,
        labelKey: 'components.menu.vanHanhGiaoHang',
        icon: <LocalShippingOutlinedIcon sx={{ fontSize: 18 }} />,
        groupKey: 'components.menu.vanHanhSection',
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_KHO_GIAO_XEM],
      },
      {
        id: 'thongBaoTrungTam',
        to: routerConstants.thongBaoTrungTam,
        labelKey: 'components.menu.thongBaoTrungTam',
        icon: <NotificationsOutlinedIcon sx={{ fontSize: 18 }} />,
        permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_THONG_BAO],
      },
    ],
  },
]

/** Menu hệ thống — hiển thị trong nút cài đặt ngoài cùng bên phải header, không nằm trên thanh menu chính. */
export const banhangSystemMenu: Extract<BanhangTopMenuEntry, { type: 'dropdown' }> = {
  id: 'systemMenu',
  type: 'dropdown',
  labelKey: 'components.menu.system',
  icon: <SettingsOutlinedIcon sx={topNavIconSx} />,
  items: [
    {
      id: 'users',
      to: routerConstants.user.list,
      labelKey: 'components.menu.staffs',
      icon: <PersonOutlineIcon sx={{ fontSize: 18 }} />,
      permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_USER_LIST],
    },
    {
      id: 'settings',
      to: routerConstants.settings,
      labelKey: 'components.menu.settings',
      icon: <SettingsOutlinedIcon sx={{ fontSize: 18 }} />,
      permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_SETTINGS_LIST],
    },
    {
      id: 'caiDatThongBao',
      to: routerConstants.settingsThongBao,
      labelKey: 'components.menu.caiDatThongBao',
      icon: <NotificationsOutlinedIcon sx={{ fontSize: 18 }} />,
      permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_THONG_BAO],
      vanHanhOnly: true,
    },
    {
      id: 'mauInHoaDon',
      to: routerConstants.settingsMauIn,
      labelKey: 'components.menu.mauInHoaDon',
      icon: <PrintOutlinedIcon sx={{ fontSize: 18 }} />,
      permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_MAU_IN_LIST],
    },
    {
      id: 'audit',
      to: routerConstants.audit.list,
      labelKey: 'components.menu.auditList',
      icon: <HistoryOutlinedIcon sx={{ fontSize: 18 }} />,
      permissions: [ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_AUDIT_LIST],
    },
    {
      id: 'logLoiHeThong',
      to: routerConstants.logLoiHeThong.list,
      labelKey: 'components.menu.logLoiHeThong',
      icon: <BugReportOutlinedIcon sx={{ fontSize: 18 }} />,
      superAdminOnly: true,
    },
    {
      id: 'uiKit',
      to: routerConstants.uiKit,
      labelKey: 'components.menu.uiKit',
      icon: <WidgetsOutlinedIcon sx={{ fontSize: 18 }} />,
    },
    uiTestSystemMenuItem,
    testModuleChiTietMenuItem,
  ],
}

const BANHANG_NAV_TOS: string[] = [...banhangTopMenu, banhangSystemMenu].flatMap((entry) =>
  entry.type === 'link' ? [entry.to] : entry.items.map((item) => item.to),
)

const allowTopMenuItem = (
  item: { permissions?: AppPermission[]; requireAllPermissions?: AppPermission[] },
  hasAnyPermission: (p: AppPermission[]) => boolean,
  hasAllPermissions?: (p: AppPermission[]) => boolean,
): boolean => {
  const { permissions, requireAllPermissions } = item
  if (requireAllPermissions && requireAllPermissions.length > 0) {
    const hasAll =
      hasAllPermissions ??
      ((codes: AppPermission[]) => codes.every((c) => hasAnyPermission([c])))
    if (!hasAll(requireAllPermissions)) return false
  }
  if (!permissions || permissions.length === 0) return true
  return hasAnyPermission(permissions)
}

export function filterBanhangTopMenuByPermissions(
  menu: BanhangTopMenuEntry[],
  hasAnyPermission: (required: AppPermission[]) => boolean,
  hasAllPermissions?: (required: AppPermission[]) => boolean,
): BanhangTopMenuEntry[] {
  return menu.reduce<BanhangTopMenuEntry[]>((acc, entry) => {
    if (entry.type === 'link') {
      if (allowTopMenuItem(entry, hasAnyPermission, hasAllPermissions)) {
        acc.push(entry)
      }
      return acc
    }
    const items = entry.items.filter((i) =>
      allowTopMenuItem(i, hasAnyPermission, hasAllPermissions),
    )
    if (items.length > 0) {
      acc.push({ ...entry, items })
    }
    return acc
  }, [])
}

export function filterBanhangNavDropdownItems(
  items: BanhangNavDropdownItem[],
  hasAnyPermission: (required: AppPermission[]) => boolean,
  hasAllPermissions?: (required: AppPermission[]) => boolean,
): BanhangNavDropdownItem[] {
  const superAdmin = isSuperAdmin()
  return items.filter(
    (item) =>
      (superAdmin || !item.superAdminOnly) &&
      (enableVanHanh || !item.vanHanhOnly) &&
      allowTopMenuItem(item, hasAnyPermission, hasAllPermissions),
  )
}

const banhangMenuPath = (to: string) => to.split('?')[0]

const banhangMenuQuery = (to: string) => to.split('?')[1] ?? ''

/** Item có query ưu tiên hơn item cùng path không query. */
const banhangMenuPathScore = (to: string) =>
  banhangMenuPath(to).replace(/\/list$/, '').length + (banhangMenuQuery(to) ? 1000 : 0)

const banhangQueryMatches = (search: string, to: string): boolean => {
  const query = banhangMenuQuery(to)
  if (!query) return true
  const current = new URLSearchParams(search)
  return [...new URLSearchParams(query)].every(([k, v]) => current.get(k) === v)
}

const banhangPathMatchesTo = (pathname: string, to: string, search = ''): boolean => {
  if (!banhangQueryMatches(search, to)) return false
  const toPath = banhangMenuPath(to)
  if (pathname === toPath) return true
  if (toPath === routerConstants.dashboard) return pathname === '/'
  if (
    toPath === routerConstants.congNo.khachHang &&
    (pathname === routerConstants.congNo.legacyList ||
      [
        routerConstants.congNo.theoDon,
        routerConstants.congNo.theoDonChiTiet,
        routerConstants.congNo.thanhToan,
        routerConstants.congNo.hoanTien,
      ].includes(pathname))
  ) {
    return true
  }
  if (
    toPath === routerConstants.bangGia.list &&
    (pathname === routerConstants.bangGiaHangHoa.list ||
      pathname.startsWith('/admin/bang-gia-hang-hoa/'))
  ) {
    return true
  }
  // Chi tiết hàng không có mục menu riêng — vẫn highlight menu Hóa đơn / Đặt hàng.
  const hoaDonChiTietBase = routerConstants.hoaDon.chiTietHang.replace(/\/list$/, '')
  if (
    toPath === routerConstants.hoaDon.list &&
    (pathname === routerConstants.hoaDon.chiTietHang ||
      pathname.startsWith(`${hoaDonChiTietBase}/`))
  ) {
    return true
  }
  const datHangChiTietBase = routerConstants.datHang.chiTietHang.replace(/\/list$/, '')
  if (
    toPath === routerConstants.datHang.list &&
    (pathname === routerConstants.datHang.chiTietHang ||
      pathname.startsWith(`${datHangChiTietBase}/`))
  ) {
    return true
  }
  // Menu báo cáo mặc định vào Pivot — màn Chi tiết (bỏ `/pivot`) vẫn highlight cùng menu.
  if (toPath.endsWith('/pivot') && pathname === toPath.slice(0, -'/pivot'.length)) {
    return true
  }
  // Bán online: 1 nút TopNav — highlight khi đứng bất kỳ màn `/admin/ban-online/*`.
  if (toPath.startsWith('/admin/ban-online/') && pathname.startsWith('/admin/ban-online/')) {
    return true
  }
  // Bỏ `/list` để nhận diện detail/setup… nhưng phải cắt theo segment —
  // tránh `/admin/bang-gia` khớp nhầm `/admin/bang-gia-hang-hoa`.
  const normalizedRoute = toPath.replace(/\/list$/, '')
  return pathname === normalizedRoute || pathname.startsWith(`${normalizedRoute}/`)
}

export function isBanhangPathActive(pathname: string, to: string, search = ''): boolean {
  if (!banhangPathMatchesTo(pathname, to, search)) return false
  // Cùng prefix (vd. `/admin/chuyen-hang/list` vs `/admin/chuyen-hang/edit`) → item khớp dài nhất thắng.
  let best = to
  let bestScore = banhangMenuPathScore(to)
  for (const other of BANHANG_NAV_TOS) {
    if (other === to || !banhangPathMatchesTo(pathname, other, search)) continue
    const score = banhangMenuPathScore(other)
    if (score > bestScore) {
      best = other
      bestScore = score
    }
  }
  return best === to
}

export function isBanhangDropdownActive(
  pathname: string,
  items: Pick<BanhangNavDropdownItem, 'to'>[],
  search = '',
): boolean {
  return items.some((item) => isBanhangPathActive(pathname, item.to, search))
}

export type BanhangMobileMenuSection = {
  subheaderKey?: string
  items: { labelKey: string; to: string; groupKey?: string }[]
}

export function buildBanhangMobileMenuSections(menu: BanhangTopMenuEntry[]): BanhangMobileMenuSection[] {
  return menu.map((entry) => {
    if (entry.type === 'link') {
      return { items: [{ labelKey: entry.labelKey, to: entry.to }] }
    }
    return {
      subheaderKey: entry.labelKey,
      items: entry.items.map((i) => ({ labelKey: i.labelKey, to: i.to, groupKey: i.groupKey })),
    }
  })
}

const HOVER_CLOSE_MS = 280

const subMenuListItemSx = {
  mx: 0.75,
  my: 0,
  py: 0.5,
  px: 1,
  minHeight: 0,
  borderRadius: `${banhangKiotLike.kiotSubmenuItemRadiusPx}px`,
  color: banhangKiotLike.topNavText,
  fontFamily: banhangKiotLike.kiotFontStack,
  '& .MuiListItemIcon-root': {
    minWidth: 28,
    color: 'inherit',
  },
  '&:hover': {
    bgcolor: 'rgba(255,255,255,0.12)',
  },
  '&.Mui-selected': {
    bgcolor: 'rgba(255,255,255,0.22)',
  },
  '&.Mui-selected:hover': {
    bgcolor: 'rgba(255,255,255,0.28)',
  },
} as const

const subMenuGroupHeaderSx = {
  lineHeight: '22px',
  fontWeight: 700,
  fontSize: '0.6875rem',
  letterSpacing: '0.04em',
  textTransform: 'uppercase' as const,
  color: 'rgba(255,255,255,0.55)',
  bgcolor: 'transparent',
  px: 1.5,
  pt: 0.75,
  pb: 0,
  fontFamily: banhangKiotLike.kiotFontStack,
} as const

/** Nhóm thứ 2 trở đi: kẻ ngang ngăn cách với nhóm trên. */
const subMenuGroupHeaderSeparatedSx = {
  ...subMenuGroupHeaderSx,
  mt: 0.5,
  pt: 1,
  borderTop: '1px solid rgba(255,255,255,0.15)',
} as const

const TopNavBar: React.FC = () => {
  const { pathname, search } = useLocation()
  const { t } = useI18n()
  const { hasAnyChucNangByCode, hasAllChucNangByCode, hasChucNangByCode } = useMenuChucNang()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const currentChiNhanhId = useCurrentChiNhanhId()
  /** Bấm menu Vận hành → kèm lọc chi nhánh đang login. */
  const menuTo = React.useCallback(
    (to: string) => withCurrentChiNhanhQuery(to, currentChiNhanhId),
    [currentChiNhanhId],
  )

  const dropdownListSx = React.useMemo(
    () => ({
      py: 0.5,
      px: 0.25,
      minWidth: 240,
      maxHeight: 'min(70vh, 480px)' as const,
      overflow: 'auto' as const,
      bgcolor: theme.palette.banhang.kiotSubmenuPanelBg,
      color: banhangKiotLike.topNavText,
      borderRadius: `${banhangKiotLike.kiotSubmenuPaperRadiusPx}px`,
      border: '1px solid rgba(255,255,255,0.28)',
      boxShadow: '0 6px 16px rgba(0,0,0,0.22)',
      boxSizing: 'border-box' as const,
    }),
    [theme.palette.banhang.kiotSubmenuPanelBg],
  )

  const [mobileAnchor, setMobileAnchor] = React.useState<null | HTMLElement>(null)
  const [openDropdown, setOpenDropdown] = React.useState<{ id: string; anchor: HTMLElement } | null>(null)

  const closeTimerRef = React.useRef<number | null>(null)
  const clearCloseTimer = React.useCallback(() => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }, [])

  const scheduleCloseDropdown = React.useCallback(() => {
    clearCloseTimer()
    closeTimerRef.current = window.setTimeout(() => {
      setOpenDropdown(null)
      closeTimerRef.current = null
    }, HOVER_CLOSE_MS)
  }, [clearCloseTimer])

  React.useEffect(() => () => clearCloseTimer(), [clearCloseTimer])

  const closeMobile = () => setMobileAnchor(null)

  const closeDropdownNow = () => {
    clearCloseTimer()
    setOpenDropdown(null)
  }

  const visibleMenu = React.useMemo(() => {
    // C1: flag off → bỏ cả dropdown "Vận hành" + "Báo cáo vận hành" (desktop + mobile dùng chung mảng này)
    const superAdmin = isSuperAdmin()
    const base = (enableVanHanh
      ? banhangTopMenu
      : banhangTopMenu.filter((entry) => entry.id !== 'vanHanhMenu')
    ).map((entry) =>
      entry.type === 'dropdown'
        ? {
            ...entry,
            items: entry.items.filter(
              (item) =>
                (superAdmin || !item.superAdminOnly) && (enableVanHanh || !item.vanHanhOnly),
            ),
          }
        : entry,
    )
    return filterBanhangTopMenuByPermissions(
      base,
      hasAnyChucNangByCode,
      hasAllChucNangByCode,
    ).map((entry) =>
      entry.id === 'banOnlineMenu' && entry.type === 'link'
        ? { ...entry, to: resolveBanOnlineLanding(hasChucNangByCode) }
        : entry,
    )
  }, [hasAnyChucNangByCode, hasAllChucNangByCode, hasChucNangByCode])

  const mobileSections = React.useMemo(() => buildBanhangMobileMenuSections(visibleMenu), [visibleMenu])

  const activeDropdownEntry = openDropdown
    ? (visibleMenu.find((e) => e.id === openDropdown.id && e.type === 'dropdown') as
        | Extract<BanhangTopMenuEntry, { type: 'dropdown' }>
        | undefined)
    : undefined

  const navR = banhangKiotLike.navItemRadiusPx
  const navHighlightColors = React.useMemo(
    () => ({
      activeBg: theme.palette.banhang.navItemActiveBg,
      activeBgHover: theme.palette.banhang.navItemActiveBgHover,
    }),
    [theme.palette.banhang.navItemActiveBg, theme.palette.banhang.navItemActiveBgHover],
  )

  const navBtn = (active: boolean) => ({
    ...buildKiotHeaderNavButtonSx(active, navHighlightColors),
    alignSelf: 'stretch' as const,
    height: '100%',
    minHeight: 0,
    '& .MuiButton-startIcon': { mr: '10px', ml: 0 },
  })

  const openDropdownFromHover = (id: string, anchor: HTMLElement) => {
    clearCloseTimer()
    setOpenDropdown({ id, anchor })
  }

  return (
    <>
      {isMobile ? (
        <IconButton
          color="inherit"
          edge="start"
          size="small"
          onClick={(e) => setMobileAnchor(e.currentTarget)}
          aria-label="Mở menu"
          sx={{
            color: '#fff',
            alignSelf: 'stretch',
            height: '100%',
            minWidth: 44,
            borderRadius: `${navR}px`,
            py: 0,
          }}
        >
          <MenuIcon fontSize="small" />
        </IconButton>
      ) : null}

      {!isMobile ? (
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            height: '100%',
            display: 'flex',
            alignItems: 'stretch',
            overflowX: 'auto',
            overflowY: 'visible',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            gap: 0,
            pl: `${banhangKiotLike.kiotHeaderMainNavOffsetLeftPx}px`,
            py: `${banhangKiotLike.kiotHeaderRightItemMarginYPx}px`,
            boxSizing: 'border-box',
          }}
        >
          {visibleMenu.map((entry) => {
            if (entry.type === 'link') {
              const active = isBanhangPathActive(pathname, entry.to, search)
              return (
                <Button
                  key={entry.id}
                  component={Link}
                  to={menuTo(entry.to)}
                  color="inherit"
                  size="small"
                  disableElevation
                  disableRipple
                  startIcon={entry.icon}
                  sx={navBtn(active)}
                >
                  {t(entry.labelKey)}
                </Button>
              )
            }
            const ddActive = isBanhangDropdownActive(pathname, entry.items, search)
            const menuOpen = openDropdown?.id === entry.id
            const looksActive = ddActive || menuOpen
            return (
              <Button
                key={entry.id}
                color="inherit"
                size="small"
                disableElevation
                disableRipple
                startIcon={entry.icon}
                sx={navBtn(looksActive)}
                onMouseEnter={(e) => openDropdownFromHover(entry.id, e.currentTarget)}
                onMouseLeave={scheduleCloseDropdown}
              >
                {t(entry.labelKey)}
              </Button>
            )
          })}
        </Box>
      ) : (
        <Box sx={{ flex: 1 }} />
      )}

      {isMobile ? (
        <Menu
          anchorEl={mobileAnchor}
          open={Boolean(mobileAnchor)}
          onClose={closeMobile}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: {
              sx: {
                maxHeight: 'min(80vh, 560px)',
                minWidth: 240,
                mt: 0.5,
                borderRadius: `${banhangKiotLike.kiotSubmenuPaperRadiusPx}px`,
                border: banhangKiotLike.filterSidebarSectionBorder,
                boxShadow: banhangKiotLike.kiotListCardShadow,
              },
            },
          }}
        >
          {mobileSections.map((section, si) => (
            <React.Fragment key={si}>
              {section.subheaderKey ? (
                <ListSubheader sx={{ lineHeight: '36px', fontWeight: 700, fontSize: '0.75rem' }}>
                  {t(section.subheaderKey)}
                </ListSubheader>
              ) : null}
              {section.items.map((item, ii) => {
                const active = isBanhangPathActive(pathname, item.to, search)
                const prevGroup = ii > 0 ? section.items[ii - 1].groupKey : undefined
                const showGroup = item.groupKey && item.groupKey !== prevGroup
                return (
                  <React.Fragment key={`${si}-${item.to}`}>
                    {showGroup ? <ListSubheader sx={subMenuGroupHeaderSx}>{t(item.groupKey!)}</ListSubheader> : null}
                    <MenuItem
                      component={Link}
                      to={menuTo(item.to)}
                      onClick={closeMobile}
                      selected={active}
                      sx={{ py: 1, pl: item.groupKey ? 2.5 : undefined }}
                    >
                      <ListItemText
                        primary={t(item.labelKey)}
                        primaryTypographyProps={{
                          fontWeight: banhangKiotLike.kiotNavFontWeight,
                          fontSize: '0.9375rem',
                        }}
                      />
                    </MenuItem>
                  </React.Fragment>
                )
              })}
            </React.Fragment>
          ))}
        </Menu>
      ) : null}

      {!isMobile && Boolean(activeDropdownEntry && openDropdown) ? (
        <Popper
          open
          anchorEl={openDropdown?.anchor ?? null}
          placement="bottom-start"
          transition
          sx={{ zIndex: (z) => z.zIndex.modal + 2 }}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={120}>
              <Paper
                elevation={0}
                onMouseEnter={clearCloseTimer}
                onMouseLeave={scheduleCloseDropdown}
                sx={dropdownListSx}
              >
                <List
                  component="nav"
                  dense
                  disablePadding
                  sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}
                >
                  {activeDropdownEntry!.items.map((item, ii) => {
                    const active = isBanhangPathActive(pathname, item.to, search)
                    const prevGroup = ii > 0 ? activeDropdownEntry!.items[ii - 1].groupKey : undefined
                    const showGroup = item.groupKey && item.groupKey !== prevGroup
                    return (
                      <React.Fragment key={item.to}>
                        {showGroup ? (
                          <ListSubheader
                            sx={ii > 0 ? subMenuGroupHeaderSeparatedSx : subMenuGroupHeaderSx}
                          >
                            {t(item.groupKey!)}
                          </ListSubheader>
                        ) : null}
                        <ListItemButton
                          component={Link}
                          to={menuTo(item.to)}
                          selected={active}
                          onClick={closeDropdownNow}
                          sx={subMenuListItemSx}
                        >
                          {item.icon ? <ListItemIcon>{item.icon}</ListItemIcon> : null}
                          <ListItemText
                            primary={t(item.labelKey)}
                            primaryTypographyProps={{
                              fontSize: `${banhangKiotLike.kiotNavFontSizePx}px`,
                              lineHeight: 1.25,
                              fontWeight: banhangKiotLike.kiotNavFontWeight,
                              color: 'inherit',
                              fontFamily: banhangKiotLike.kiotFontStack,
                            }}
                          />
                        </ListItemButton>
                      </React.Fragment>
                    )
                  })}
                </List>
              </Paper>
            </Fade>
          )}
        </Popper>
      ) : null}
    </>
  )
}

export default TopNavBar
